import './FieldUpdateMetadataTab.scss';

import { CodeHighlight } from '@ui/general';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { coreActions } from '@sonar-software/sonar-core';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { PeriscopeFieldRow } from '@periscopes/general/fields';
import { SalesforceDescription } from '@ui/text';

import { DetailSection, DetailSubsection, InformationSection } from '@periscopes/general';
import { MetadataContainer, MetadataEntry } from '@periscopes/general/metadata';
import {
	PeriscopeSfFieldSummary,
	PeriscopeSfFieldUpdateMetadata,
	PeriscopeSfFieldUpdateReferences,
	SonarObjectType
} from '@sonar-software/sonar-ts-types';
import React, { useEffect, useMemo } from 'react';
import { useAction, useApiResource, usePeriscopeMetadata } from '@hooks';

export interface FieldUpdateMetadataTabProps {
	index: number;
	sonarObjectId: number;
}

export const FieldUpdateMetadataTab: React.FC<FieldUpdateMetadataTabProps> = ({ index, sonarObjectId }) => {
	const updateReferences = useAction(coreActions.updateSonarObjectReferences);

	const { loading, metadata } = usePeriscopeMetadata<PeriscopeSfFieldUpdateMetadata>(
		sonarObjectId,
		SonarObjectType.SalesforceFieldUpdate
	);

	const { loading: loadingRefs, resource: refsResource } = useApiResource<PeriscopeSfFieldUpdateReferences>(
		`v3/salesforce/field-updates/${sonarObjectId}/references`
	);

	const metadataEntries = useMemo(() => {
		if (!metadata) {
			return [];
		}

		const entries: MetadataEntry[] = [
			{
				label: COPY_SPECIFIC.OBJECT,
				type: 'string',
				value: metadata.object
			},
			{
				label: COPY_SPECIFIC.RE_EVALUATE_AFTER_FIELD_CHANGE,
				type: 'boolean',
				value: metadata.shouldReevaluateOnChange
			}
		];

		return entries;
	}, [ metadata ]);

	useEffect(() => {
		if (refsResource?.field) {
			updateReferences([
				{
					displayName: refsResource.field.displayName,
					ingestedBy: refsResource.field.ingestedBy,
					parentDisplayName: refsResource.field.parentDisplayName,
					parentSonarObjectId: refsResource.field.parentSonarObjectId,
					populatedBy: refsResource.field.populatedBy,
					relatedCounts: refsResource.field.relatedCounts,
					sonarObjectId: refsResource.field.sonarObjectId,
					sonarObjectType: SonarObjectType.SalesforceField,
					tags: refsResource.field.tagIds
				}
			]);
		}
	}, [ refsResource?.field, updateReferences ]);

	if (loading || loadingRefs) {
		return <LoadingSpinner spinning />;
	}

	return (
		<>
			<InformationSection sonarObjectId={ sonarObjectId }>
				<SalesforceDescription sonarObjectId={ sonarObjectId } />
			</InformationSection>
			<DetailSection
				className='field-update-details'
				spacing='small'>
				<MetadataContainer
					entries={ metadataEntries }
					title={ COPY_SPECIFIC.WORKFLOW_DETAIL } />
				<DetailSubsection
					spacing='none'
					title={ COPY_SPECIFIC.FIELD_TO_UPDATE }>
					<PeriscopeFieldRow
						field={ refsResource?.field as PeriscopeSfFieldSummary }
						parentIndex={ index }
						useApiNames={ false } />
				</DetailSubsection>
				<DetailSubsection
					spacing='none'
					title={ refsResource?.isFormula ? COPY_SPECIFIC.FORMULA : COPY_SPECIFIC.VALUE }>
					<CodeHighlight
						code={ refsResource?.value ?? '' }
						codeReferences={ refsResource?.codeReferences ?? [] }
						index={ index } />
				</DetailSubsection>
			</DetailSection>
		</>
	);
};
